import { Badge, Flex, Select, Tag, type SelectProps } from "antd"
import classNames from "classnames"
import {
  DEFAULT_VIDEO_CHANNEL,
  VIDEO_CHANNEL_OPTIONS,
} from "~/common/constants"
import { CubeModelIcon, Text } from "~/components"
import { type VideoModelDisabledReasons } from "~/common/video/videoModels"
import styles from "./index.css"

type VideoModelOption = (typeof VIDEO_CHANNEL_OPTIONS)[number] & {
  disabledReason?: string
}

type VideoModelSelectProps = Omit<
  SelectProps<API.Task.CreateVideoChannel, VideoModelOption>,
  "onChange" | "options" | "value"
> & {
  value: API.Task.CreateVideoChannel
  onChange: (value: API.Task.CreateVideoChannel) => void
  disabledReasons?: VideoModelDisabledReasons
}

const ModelTag = ({ option }: { option: VideoModelOption }) => {
  return (
    <Text
      size="s"
      className={classNames(styles.modelTag, styles[option.tone])}
    >
      {option.tag}
    </Text>
  )
}

const SelectedModel = ({ option }: { option: VideoModelOption }) => {
  return (
    <Flex
      align="center"
      gap={8}
    >
      <Text>{option.label}</Text>
      <ModelTag option={option} />
    </Flex>
  )
}

const ModelOption = ({ option }: { option: VideoModelOption }) => {
  return (
    <Flex gap={12}>
      <Badge
        color={option.color}
        className={styles.dot}
      />
      <Flex vertical>
        <Flex
          align="center"
          gap={8}
        >
          <Text>{option.label}</Text>
          <ModelTag option={option} />
          {option.isNew ? <Tag color="red">NEW</Tag> : null}
        </Flex>
        <Text
          size="s"
          type="secondary"
        >
          {option.disabledReason || option.description}
        </Text>
      </Flex>
    </Flex>
  )
}

export const VideoModelSelect = ({
  className,
  onChange,
  value,
  disabledReasons,
  ...props
}: VideoModelSelectProps) => {
  const options = VIDEO_CHANNEL_OPTIONS.map((option) => {
    const disabledReason = disabledReasons?.[option.value]
    return Object.assign({}, option, {
      disabledReason,
    })
  })
  const selectedOption =
    options.find((option) => option.value === value) ??
    options.find((option) => option.value === DEFAULT_VIDEO_CHANNEL)!

  return (
    <Badge
      className={styles.modelBadge}
      color="#ff6a00"
      dot
      offset={[-2, 2]}
    >
      <Select<API.Task.CreateVideoChannel, VideoModelOption>
        aria-label="视频模型"
        menuItemSelectedIcon={null}
        listHeight={500}
        popupMatchSelectWidth={360}
        showSearch={false}
        virtual={false}
        {...props}
        className={classNames(styles.modelSelect, className)}
        classNames={{
          popup: {
            listItem: styles.option,
          },
        }}
        optionRender={(option) => <ModelOption option={option.data} />}
        options={options}
        prefix={<CubeModelIcon fontSize={20} />}
        value={selectedOption.value}
        labelRender={() => <SelectedModel option={selectedOption} />}
        onChange={onChange}
      />
    </Badge>
  )
}
